import axiosClient from '../axiosClient';
import type { AxiosRequestConfig } from 'axios';
import type { TableDefinitionResponse } from '@/api/contracts/tableDefinitionsCore';
import type { MenuTargetType } from '@/menu/menuTargetRegistry';
import { downloadBlob } from '@/utils/download';

export type { TableDefinitionResponse };

// Interfaces
export interface MenuItem {
  localNodeId?: number;
  menuItemKey: string;
  publicId?: string;
  name: string;
  description?: string | null;
  objectType: 'folder' | 'shortcut' | 'system-object' | 'database-object' | 'report' | 'data-view';
  parentId: number | null;
  parentMenuItemKey?: string | null;
  /** Power BI rapport-GUID når objectType er report. */
  reportId?: string;
  workspaceId?: string;
  /** ViewId (Config.DataView) når objectType er data-view. */
  dataViewId?: string;
  /** True når datavisningen er system-generert (fra DataModel.isSystemGenerated). */
  isSystemGeneratedDataView?: boolean;
  targetType?: MenuTargetType;
  targetPublicId?: string;
  targetSubType?: string | null;
  iconType?: string | null;
  behaviorType?: string | null;
  /** Visningsnavn for snarveismål (backend). */
  targetDisplayName?: string | null;
  shortcutTargets?: Array<{
    targetType: string;
    targetPublicId: string;
    targetSubType?: string | null;
    iconType?: string | null;
    behaviorType?: string | null;
    targetDisplayName?: string | null;
    isDefault: boolean;
    openInNewTab?: boolean;
    description?: string | null;
  }>;
  items?: MenuItem[];
}

/**
 * Hierarkisk menynode for V3-endepunktet (`GET /api/v3/menu`). Speiler
 * `ICS.Contracts.Menu.V3.MenuHierarchyNode` (camelCase). Brukes av ny app-shell
 * drill-down-meny — arbitrary depth, eksplisitt `isLeaf` og `categoryLabel`
 * for footer-chips.
 */
export interface MenuHierarchyNode {
  id: string;
  label: string | null;
  description: string | null;
  icon: string | null;
  isLeaf: boolean;
  categoryLabel: string | null;
  objectType: string | null;
  behaviorType: string | null;
  targetType: string | null;
  targetPublicId: string | null;
  targetSubType: string | null;
  reportId: string | null;
  dataViewId: string | null;
  children: MenuHierarchyNode[];
}

export interface SystemObjectSavePayload {
  menuItemKey?: string;
  publicId?: string;
  name: string;
  description?: string | null;
  objectType: string;
  parentId?: number | null;
  parentMenuItemKey?: string | null;
  targetType?: string;
  targetPublicId?: string;
  targetSubType?: string | null;
}

export interface SystemObjectOrderPayload {
  menuItemKey: string;
  ordinal: number;
}

export interface SystemObjectTargetPayload {
  targetType: string;
  targetPublicId: string;
  targetSubType?: string | null;
}

// API Functions
export async function fetchMenu (signal?: AbortSignal): Promise<MenuItem[]> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.get<MenuItem[]>('v2/menu', config);
  const items = response.data ?? [];
  const idByKey = new Map<string, number>();
  let nextId = 1;

  const assignIds = (nodes: MenuItem[], parentId: number | null, parentMenuItemKey: string | null): MenuItem[] =>
    nodes.map((node) => {
      const key = node.menuItemKey || node.publicId || `__tmp_${nextId}`;
      const existing = idByKey.get(key);
      const localNodeId = existing ?? nextId++;
      idByKey.set(key, localNodeId);
      const currentMenuItemKey = String(node.menuItemKey || '').trim() || null;
      const normalizedItems = Array.isArray(node.items)
        ? assignIds(node.items, localNodeId, currentMenuItemKey)
        : undefined;
      return {
        ...node,
        localNodeId,
        parentId,
        parentMenuItemKey,
        items: normalizedItems,
      };
    });

  return assignIds(items, null, null);
}

/**
 * Henter hierarkisk menystruktur fra V3-endepunktet. Brukes av ny app-shell
 * (`appShellMenuStore`) for drill-down. Gammel `fetchMenu` (V2) er urørt.
 */
export async function fetchMenuHierarchy (signal?: AbortSignal): Promise<MenuHierarchyNode[]> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.get<MenuHierarchyNode[]>('v3/menu', config);
  return response.data ?? [];
}

export async function saveSystemObject (
  item: SystemObjectSavePayload,
  signal?: AbortSignal
): Promise<string> {
  const config: AxiosRequestConfig = { signal };
  const payload: SystemObjectSavePayload = {
    ...item,
    menuItemKey: item.menuItemKey || item.publicId,
  };
  const response = await axiosClient.post<string>('v2/system-object', payload, config);
  return response.data;
}

export async function getSystemObjectTargets (
  menuItemKey: string,
  signal?: AbortSignal
): Promise<MenuItem['shortcutTargets']> {
  const config: AxiosRequestConfig = { signal };
  const response = await axiosClient.get<MenuItem['shortcutTargets']>(`v2/system-object/${menuItemKey}/targets`, config);
  return response.data ?? [];
}

export async function addSystemObjectTarget (
  menuItemKey: string,
  request: SystemObjectTargetPayload,
  signal?: AbortSignal
): Promise<void> {
  const config: AxiosRequestConfig = { signal };
  await axiosClient.post(`v2/system-object/${menuItemKey}/targets`, request, config);
}

export async function removeSystemObjectTarget (
  menuItemKey: string,
  request: SystemObjectTargetPayload,
  signal?: AbortSignal
): Promise<void> {
  const config: AxiosRequestConfig = { signal };
  await axiosClient.post(`v2/system-object/${menuItemKey}/targets/remove`, request, config);
}

export async function setDefaultSystemObjectTarget (
  menuItemKey: string,
  request: SystemObjectTargetPayload,
  signal?: AbortSignal
): Promise<void> {
  const config: AxiosRequestConfig = { signal };
  await axiosClient.post(`v2/system-object/${menuItemKey}/targets/default`, request, config);
}

export async function deleteSystemObject (
  menuItemKey: string,
  signal?: AbortSignal
): Promise<void> {
  const config: AxiosRequestConfig = { signal };
  await axiosClient.delete(`v2/system-object/${menuItemKey}`, config);
}

export async function undeleteSystemObject (
  menuItemKey: string,
  signal?: AbortSignal
): Promise<void> {
  const config: AxiosRequestConfig = { signal };
  await axiosClient.post(`v2/system-object/${menuItemKey}/undelete`, undefined, config);
}

export async function saveSystemObjectOrder (
  items: SystemObjectOrderPayload[],
  signal?: AbortSignal
): Promise<void> {
  const config: AxiosRequestConfig = { signal };
  const payload = items
    .filter((x) => !!x.menuItemKey)
    .map((x) => ({ menuItemKey: x.menuItemKey, ordinal: x.ordinal }));
  await axiosClient.post('v2/system-object/order', payload, config);
}

export async function fetchTableDefinition (
  menuItemKey: string,
  signal?: AbortSignal
): Promise<TableDefinitionResponse | null> {
  const config: AxiosRequestConfig = {
    signal,
    params: { menuItemKey },
  };
  const response = await axiosClient.get<TableDefinitionResponse>('v2/table', config);
  return response.data ?? null;
}

export interface MenuStructureCreateItemDto {
  parentPath: string;
  name: string;
  objectType: string;
  ordinal: number;
}

export interface MenuStructurePreviewResponse {
  toCreate: MenuStructureCreateItemDto[];
  duplicatesSkipped: string[];
  errors: string[];
  canApply: boolean;
  selectedSheetName?: string | null;
  suggestedSheetName?: string | null;
  availableSheetNames?: string[] | null;
}

export interface MenuStructureApplyRequest {
  rootMenuItemKey: string;
  items: MenuStructureCreateItemDto[];
}

export interface MenuStructureApplyResponse {
  createdCount: number;
  createdMenuItemKeys: string[];
}

export async function downloadMenuStructureExport (
  rootMenuItemKey: string,
  format: 'json' | 'xlsx',
  options?: { includeAnchor?: boolean; signal?: AbortSignal }
): Promise<void> {
  const { includeAnchor = false, signal } = options ?? {};
  const url = `v2/menu/export/${rootMenuItemKey}/${format}`;
  const params = includeAnchor ? { includeAnchor: true } : undefined;
  const response = await axiosClient.get(url, { responseType: 'blob', signal, params });
  const contentType = response.headers['content-type'];
  const blob = new globalThis.Blob([response.data], {
    type: typeof contentType === 'string' ? contentType : 'application/octet-stream'
  });
  const contentDisposition = response.headers['content-disposition'] as string | undefined;
  let filename = `menu-structure-${rootMenuItemKey}.${format === 'xlsx' ? 'xlsx' : 'json'}`;
  if (contentDisposition) {
    const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(contentDisposition);
    const extracted = match?.[1] || match?.[2];
    if (extracted) filename = decodeURIComponent(extracted);
  }
  downloadBlob(blob, filename);
}

export async function previewMenuStructureImport (
  rootMenuItemKey: string,
  file: File,
  sheetName?: string | null,
  signal?: AbortSignal
): Promise<MenuStructurePreviewResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('rootMenuItemKey', String(rootMenuItemKey));
  if (sheetName) {
    formData.append('sheetName', sheetName);
  }
  const response = await axiosClient.post<MenuStructurePreviewResponse>(
    'v2/menu/import/preview',
    formData,
    {
      signal,
      headers: { 'Content-Type': 'multipart/form-data' }
    }
  );
  return response.data;
}

export async function applyMenuStructureImport (
  request: MenuStructureApplyRequest,
  signal?: AbortSignal
): Promise<MenuStructureApplyResponse> {
  const response = await axiosClient.post<MenuStructureApplyResponse>(
    'v2/menu/import/apply',
    request,
    { signal }
  );
  return response.data;
}
